<script lang="ts">
    import MeasurementsPage from '@Criteria/Hierarchies/Pages/MeasurementsPage'
    export default MeasurementsPage
</script>

<template>
    <div>
        <CriterionSelectorDialog :viewModel="criterionSelectorDialog" 
                                 @confirm-selection="changeCriterion" />

        <div class="modal-mask" v-if="criterionEditorOpened === true">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-lg modal-dialog-scrollable" style="min-height: 600px;">
                    <div class="modal-content" v-if="editedCriterion">
                        <div class="modal-header">
                            <h5 class="modal-title">Criterion: {{editedCriterion.Name}}</h5>
                            <button type="button" class="close" @click="closeCriterionEditor">&times;</button>
                        </div>
                        <div class="modal-body">
                            <div class="form-group">
                                <label>Linked attribute:</label>
                                <div class="input-group">
                                    <input type="text" class="form-control" readonly v-model="editedCriterion.attributeNameOrNone" />
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">Choose</button>
                                        <div class="dropdown-menu dropdown-menu-right" style="background-color: #F5F5F5; width: 70%;">
                                            <div class="px-2 py-1">
                                                <button class="btn btn-secondary btn-sm" @click="showAttributeCreator()">Create new attribute</button>
                                                <button class="btn btn-secondary btn-sm" @click="editedCriterion.unlink()" :disabled="!editedCriterion.AttributeId">Unlink attribute</button>
                                            </div>
                                            <div class="dropdown-divider"></div>
                                            <form class="px-3 py-1">
                                                <div class="form-group">
                                                    <input v-model="SearchAttributePhrase" type="text" class="form-control" placeholder="search">
                                                </div>
                                            </form>
                                            <div style="max-height: 200px; overflow-y: auto; overflow-x: hidden;">
                                                <div v-for="s in AttributesSections">
                                                    <h6 class="dropdown-header" v-if="s.Section">{{s.Section}}</h6>
                                                    <a v-for="attr in s.Attributes" :key="attr.Id" @click="editedCriterion.link(attr.Id)" class="dropdown-item">{{attr.Name}}</a>
                                                    <div class="dropdown-divider"></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label><span v-if="editedCriterion.defaultMax == null" class="text-danger">*</span>Normalization cap:</label>
                                <input type="number" class="form-control" v-model="editedCriterion.MaxValue" :placeholder="editedCriterion.defaultMax" />
                                <p class="form-text text-muted">
                                    Criteria must have a maximum value, beyond which no extra value is given in calculating normalized score.
                                    Scores are calculated as a ratio of this Normalization cap. It is usually defined as the highest value in a scale.
                                </p>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button @click="handleUpdateCriterionMeasurementCommand" type="button" class="btn btn-primary">Confirm</button>
                            <button @click="closeCriterionEditor" type="button" class="btn btn-outline-secondary">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <AttributeEditorDialog :viewModel="attributeEditorDialog"
                               @numeric-attribute-created="numericAttributeCreated"
                               @numeric-attribute-updated="numericAttributeUpdated" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <h3 v-if="!Initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>
            <div v-if="Initialized">
                <a class="btn btn-outline-secondary float-right" href="https://www.transparentchoice.com/user-guide/build-criteria" title="User guide: Build criteria" target="_blank">
                    <i class="fa fa-info" aria-hidden="true"></i>
                </a>
                <h3>Goal: {{rm.GoalName}}</h3>
                <br />
                <GoalNavigation :WorkspaceId="workspaceId" :ProjectId="projectId" :EvalModelId="evalModelId" :ActivePage="'measurement'" :ScoringEnabled="rm.ScoringEnabled"></GoalNavigation>

                <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">
                    <div class="btn-group mr-auto" role="group">
                        <button type="button" class="btn btn-outline-secondary" title="Expand all" @click="expandAll">
                            <i class="fa fa-expand" aria-hidden="true"></i>
                        </button>
                        <button type="button" class="btn btn-outline-secondary" title="Collapse all" @click="collapseAll">
                            <i class="fa fa-compress" aria-hidden="true"></i>
                        </button>
                    </div>

                    <span class="navbar-text">Filter by: <a class="text-primary" @click="showCriterionSelector">{{ FilterByCriterion?.Name ?? "All criteria" }}</a></span>

                    <form class="form-inline my-2 my-lg-0 ml-2" role="search">
                        <div class="input-group input-group-sm">
                            <input v-model="SearchPhrase" class="form-control form-control-sm" type="search" placeholder="Search">
                            <span class="input-group-append"><span class="input-group-text"><i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i></span></span>
                        </div>
                    </form>
                </nav>
                <br />

                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th style="width: 60px;"></th>
                            <th style="width: 50%;">Criterion</th>
                            <th>Linked attribute</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="c in BottomCriteriaView" :key="c.Id">
                            <td>
                                <button class="btn" @click="showCriterionEditor(c.Id)" type="button" :class="{'btn-danger': c.IsValid === false, 'btn-success': c.IsValid === true}">
                                    <i class="fa" :class="{'fa-unlink': c.Attribute == null, 'fa-link': c.Attribute != null}" aria-hidden="true"></i>
                                </button>
                            </td>
                            <td>
                                <h5 class="pointer" @click="c.Expanded = !c.Expanded">
                                    <span>{{c.Criterion.Name}}</span><br />
                                    <small class="text-muted">{{c.Criterion.Path}}</small><br />
                                    <small>Normalization cap: {{c.Criterion.MaxValue?.toString() ?? 'Not set'}}</small>
                                </h5>

                                <div v-if="c.Expanded">
                                    <p>{{c.Criterion.Description}}</p>
                                </div>
                            </td>
                            <td>
                                <div v-if="c.Attribute != null">
                                    <h5 class="pointer" @click="c.Expanded = !c.Expanded">{{c.Attribute.Name}}</h5>
                                    <div v-if="c.Expanded" class="mt-3">
                                        <p>{{c.Attribute.Description}}</p>
                                        <div v-if="c.Attribute.IsList === true">
                                            <p v-for="dv in c.Attribute.DefinedValues" :key="dv.Name">
                                                <strong>{{dv.Value}} - {{dv.Name}}</strong><br />
                                                <span>{{dv.Description}}</span>
                                            </p>
                                        </div>

                                        <button class="btn btn-sm btn-primary" @click="showAttributeEditor(c.Attribute)">Edit attribute</button>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div class="alert alert-danger">
                    <p>
                        NEGATIVE numbers will be rounded to zero in any calculations.
                        Remember, you could use calculated attributes to transform your values so they are all positive.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
